/**
 * Lazy analytics client
 * Loads PostHog only when analytics are configured and an event is captured.
 */

type AnalyticsPayload = Record<string, string | number | boolean | null | undefined>;

let posthogPromise: Promise<(typeof import('posthog-js'))['default'] | null> | null = null;
let hasInitialized = false;

function normalizeCarrier(carrier: string | null): string {
  if (!carrier) return 'unknown';

  const normalized = carrier.toUpperCase();

  if (normalized.includes('UPS')) return 'UPS';
  if (normalized.includes('FEDEX') || normalized.includes('FED EX')) return 'FedEx';
  if (normalized.includes('USPS') || normalized.includes('POSTAL')) return 'USPS';
  if (normalized.includes('DHL')) return 'DHL';
  if (normalized.includes('AMAZON') || normalized.includes('AMZL')) return 'Amazon';

  return carrier;
}

async function getPosthog() {
  if (typeof window === 'undefined') return null;

  if (!posthogPromise) {
    posthogPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
        const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

        if (!posthogKey) {
          return null;
        }

        const posthogState = posthog as typeof posthog & { __loaded?: boolean };
        if (!posthogState.__loaded && !hasInitialized) {
          posthog.init(posthogKey, {
            api_host: posthogHost || 'https://us.i.posthog.com',
            capture_pageview: false,
            capture_pageleave: false,
            persistence: 'memory',
            autocapture: false,
            capture_exceptions: false,
            enable_heatmaps: false,
            enable_recording_console_log: false,
            disable_session_recording: true,
          });
          hasInitialized = true;
        }

        return posthog;
      })
      .catch(() => null);
  }

  return posthogPromise;
}

async function capture(event: string, properties?: AnalyticsPayload) {
  const posthog = await getPosthog();
  if (!posthog) return;
  posthog.capture(event, properties);
}

export async function initAnalytics() {
  await getPosthog();
}

export async function trackPageview() {
  await capture('$pageview');
}

export async function trackTrackingViewed(data: {
  trackingNumber: string;
  carrier: string | null;
  status: string;
  hasEstimatedDelivery: boolean;
}) {
  await capture('tracking_viewed', {
    carrier: normalizeCarrier(data.carrier),
    status: data.status,
    has_estimated_delivery: data.hasEstimatedDelivery,
    tracking_prefix: data.trackingNumber.substring(0, 4),
  });
}

export async function trackTrackingNotFound(trackingNumber: string) {
  await capture('tracking_not_found', {
    tracking_prefix: trackingNumber.substring(0, 4),
  });
}

export async function trackOrderLookupAttempt() {
  await capture('order_lookup_attempted');
}

export async function trackOrderLookupSuccess(_orderNumber: string, shipmentCount: number) {
  await capture('order_lookup_success', {
    shipment_count: shipmentCount,
  });
}

export async function trackOrderLookupFailed(reason: string) {
  await capture('order_lookup_failed', {
    reason,
  });
}

export async function trackShipmentClicked(carrier: string | null) {
  await capture('shipment_clicked', {
    carrier: normalizeCarrier(carrier),
  });
}
